import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { graphqlQuery } from '@/lib/api/graphql';
import { buildGraphQLSortVariable as buildSort, type GraphQLConnection, pageToCursor } from '@/lib/graphql-pagination';
import { getFilteredGlobalContactsUI } from '@/lib/demo-data/global-contacts';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import { useMode } from '@/providers/ModeProvider';
import { deleteContactDemo, updateContactDemo } from '@/services/demo/contacts-demo.service';
import type { GlobalContactsQueryParams, GlobalContactsResultUI, GlobalContactUI } from '@/types/ui/customer-detail';
import { useApi } from './useApi';
import { useModeAwareMutation, useModeAwareQuery } from './useModeAwareQuery';

interface ContactUpdateInput {
  title?: string;
  firstName?: string;
  surname?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  preferredContactMethod?: string;
  jobTitle?: string;
  company?: string;
  notes?: string;
  isPrimary?: boolean;
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 25;

// --- GraphQL types ---

interface GlobalContactGraphQLNode {
  id: number;
  customerId: number;
  customerName: string | null;
  customerAccountNumber: string | null;
  title: string | null;
  firstName: string | null;
  surname: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  preferredContactMethod: string | null;
  jobTitle: string | null;
  company: string | null;
  notes: string | null;
  isPrimary: boolean;
  lastContactDate: string | null;
  createdAt: string;
}

interface GlobalContactListGraphQLResponse {
  globalContacts: {
    globalContactList: GraphQLConnection<GlobalContactGraphQLNode>;
  };
}

interface GlobalContactCountGraphQLResponse {
  globalContacts: {
    globalContactCount: number;
  };
}

// --- GraphQL queries ---

const GLOBAL_CONTACT_LIST_QUERY = `
  query GlobalContactList($first: Int, $after: String, $sort: [GlobalContactListSortSortByInput!], $filter: GlobalContactListFilterInput) {
    globalContacts {
      globalContactList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            id
            customerId
            customerName
            customerAccountNumber
            title
            firstName
            surname
            email
            phone
            mobile
            preferredContactMethod
            jobTitle
            company
            notes
            isPrimary
            lastContactDate
            createdAt
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

const GLOBAL_CONTACT_COUNT_QUERY = `
  query GlobalContactCount($filter: GlobalContactListFilterInput) {
    globalContacts {
      globalContactCount(filter: $filter)
    }
  }
`;

// --- Sort / filter builders ---

const sortFieldMap: Record<string, string> = {
  firstName: 'FirstName',
  surname: 'Surname',
  email: 'Email',
  phone: 'Phone',
  mobile: 'Mobile',
  jobTitle: 'JobTitle',
  company: 'Company',
  customerName: 'CustomerName',
  customerAccountNumber: 'CustomerAccountNumber',
  lastContact: 'LastContactDate',
  createdAt: 'CreatedAt',
};

function buildGraphQLSortVariable(sortField?: string, sortDirection?: 'asc' | 'desc') {
  return buildSort(sortFieldMap, sortField, sortDirection);
}

/**
 * Builds the GraphQL filter variable for the global contacts list/count queries
 * and the server-side export job, so all three stay consistent.
 */
export function buildGlobalContactsFilterVariable(params: GlobalContactsQueryParams) {
  const filter: Record<string, unknown> = {};

  if (params.search?.trim()) {
    filter.summary = params.search.trim();
  }
  if (params.customerIds && params.customerIds.length > 0) {
    filter.customerId = { containsValues: params.customerIds.map((id) => Number(id)) };
  }
  if (params.riskLevelIds && params.riskLevelIds.length > 0) {
    filter.riskLevelId = { containsValues: params.riskLevelIds };
  }
  if (params.primaryOnly) {
    filter.isPrimary = { eq: true };
  }
  if (params.hasEmail) {
    filter.hasEmail = { eq: true };
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

// --- Mapping ---

function mapGraphQLNodeToUi(node: GlobalContactGraphQLNode): GlobalContactUI {
  return {
    id: String(node.id),
    title: node.title ?? undefined,
    firstName: node.firstName ?? '',
    surname: node.surname ?? '',
    email: node.email ?? '',
    phone: node.phone ?? '',
    mobile: node.mobile ?? undefined,
    preferredContactMethod: node.preferredContactMethod ?? undefined,
    jobTitle: node.jobTitle ?? '',
    company: node.company ?? '',
    customerId: String(node.customerId),
    address: '',
    notes: node.notes ?? '',
    createdAt: node.createdAt,
    lastContact: node.lastContactDate,
    isPrimary: node.isPrimary,
    customerName: node.customerName ?? '',
    customerAccountNumber: node.customerAccountNumber ?? '',
  };
}

// --- Hook ---

/**
 * Mode-aware query hook for the Global Contacts screen. In demo mode it filters
 * and paginates the mock dataset client-side; in live mode it drives the GraphQL
 * `globalContacts.globalContactList` connection (server-side filter/sort/paging)
 * plus a `globalContactCount` query for the total, following the app's report-screen
 * pattern (rapid-read repository over the `v_GlobalContact_List` view).
 */
export function useAllContactsGlobal(params: GlobalContactsQueryParams) {
  const apiConfig = useApiConfig();

  const page = params.page ?? DEFAULT_PAGE;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const filter = buildGlobalContactsFilterVariable(params);

  const filterKey = JSON.stringify(filter ?? null);
  const sortKey = `${params.sortField ?? 'surname'}:${params.sortDirection ?? 'asc'}`;

  const demoFiltered = getFilteredGlobalContactsUI(params);
  const demoStart = (page - 1) * pageSize;
  const demoContacts = demoFiltered.slice(demoStart, demoStart + pageSize);

  const listQuery = useModeAwareQuery<GlobalContactUI[], Error>(
    ['global-contacts-list', filterKey, sortKey, page, pageSize],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {
        first: pageSize,
        sort: buildGraphQLSortVariable(params.sortField, params.sortDirection),
        filter,
      };
      if (page > 1) {
        variables.after = pageToCursor(page);
      }

      const response = await graphqlQuery<GlobalContactListGraphQLResponse>(
        apiConfig,
        GLOBAL_CONTACT_LIST_QUERY,
        variables,
        signal,
      );

      return response.globalContacts.globalContactList.edges.map((x) => mapGraphQLNodeToUi(x.node));
    },
    demoContacts,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );

  const countQuery = useModeAwareQuery<number, Error>(
    ['global-contacts-count', filterKey],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<GlobalContactCountGraphQLResponse>(
        apiConfig,
        GLOBAL_CONTACT_COUNT_QUERY,
        variables,
        signal,
      );

      return response.globalContacts.globalContactCount;
    },
    demoFiltered.length,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );

  const data: GlobalContactsResultUI = {
    contacts: listQuery.data ?? [],
    totalCount: countQuery.data ?? 0,
    page,
    pageSize,
  };

  return { data, isLoading: listQuery.isLoading, error: listQuery.error };
}

/**
 * Mutations for the Global Contacts screen. Contacts remain customer-scoped, so
 * these reuse the existing per-customer endpoints and additionally invalidate
 * the global contacts query. In demo mode the mutations apply to the demo
 * contacts store instead of calling the API.
 */
export function useGlobalContactMutations() {
  const { apiPut, apiDelete } = useApi();
  const { isDemo } = useMode();
  const queryClient = useQueryClient();

  // Demo queries capture their mock data per render, so an invalidated refetch
  // resolves with the snapshot taken before the mutation; dropping the queries
  // makes the next fetch read the mutated demo store.
  const refresh = useCallback(
    (queryKeys: unknown[][]) => {
      for (const queryKey of queryKeys) {
        if (isDemo) {
          queryClient.removeQueries({ queryKey });
        } else {
          queryClient.invalidateQueries({ queryKey });
        }
      }
    },
    [queryClient, isDemo],
  );

  const refreshForCustomer = useCallback(
    (customerId: string) =>
      refresh([
        ['global-contacts-list'],
        ['global-contacts-count'],
        ['customer-contacts', customerId],
        ['customer-detail', customerId],
      ]),
    [refresh],
  );

  const { mutateAsync: updateContactAsync } = useModeAwareMutation<
    void,
    Error,
    { customerId: string; contactId: string; contact: ContactUpdateInput }
  >(
    async ({ customerId, contactId, contact }) => {
      await apiPut<unknown>(`/api/v1/customers/${customerId}/contacts/${contactId}`, contact);
    },
    updateContactDemo,
    { onSuccess: (_data, { customerId }) => refreshForCustomer(customerId) },
  );

  const { mutateAsync: deleteContactAsync } = useModeAwareMutation<
    void,
    Error,
    { customerId: string; contactId: string }
  >(
    async ({ customerId, contactId }) => {
      await apiDelete<void>(`/api/v1/customers/${customerId}/contacts/${contactId}`);
    },
    deleteContactDemo,
    { onSuccess: (_data, { customerId }) => refreshForCustomer(customerId) },
  );

  const updateContact = useCallback(
    (customerId: string, contactId: string, contact: ContactUpdateInput) =>
      updateContactAsync({ customerId, contactId, contact }),
    [updateContactAsync],
  );

  const deleteContact = useCallback(
    (customerId: string, contactId: string) => deleteContactAsync({ customerId, contactId }),
    [deleteContactAsync],
  );

  const setContactAsPrimary = useCallback(
    (customerId: string, contactId: string) => updateContact(customerId, contactId, { isPrimary: true }),
    [updateContact],
  );

  const refreshGlobalContacts = useCallback(
    () => refresh([['global-contacts-list'], ['global-contacts-count']]),
    [refresh],
  );

  return { updateContact, deleteContact, setContactAsPrimary, refreshGlobalContacts };
}
